/**
 * S3DownloadService - Common utility for downloading files from S3 (or any URL)
 * with progress tracking. Pairs with S3UploadService.js.
 *
 * Uses XHR with responseType: 'arraybuffer' for raw byte transfer (avoids any
 * browser blob/streaming quirks). Progress percentage comes from xhr.onprogress.
 *
 * Usage:
 *   import S3DownloadService from "../../Utility/Services/S3DownloadService";
 *
 *   const blob = await S3DownloadService.downloadBlob(presignedUrl, (percent) => {
 *       this.setState({ progress: percent });
 *   });
 *
 *   const base64 = await S3DownloadService.downloadAsBase64(presignedUrl, onProgress);
 *   const objectUrl = await S3DownloadService.downloadAsObjectUrl(presignedUrl, onProgress);
 */
class S3DownloadService {

    /**
     * Download a file as a Blob with progress callback.
     * @param {string} url - The (presigned) URL to fetch
     * @param {function} [onProgress] - Callback (percent: 0-100). Called with -1 if length is unknown.
     * @param {string} [forceContentType] - Force the resulting Blob's MIME type
     * @param {AbortSignal} [signal] - Optional AbortSignal to cancel the in-flight request
     * @returns {Promise<Blob>}
     */
    downloadBlob = (url, onProgress = null, forceContentType = null, signal = null) => {
        return new Promise((resolve, reject) => {
            if (signal && signal.aborted) {
                reject(new DOMException("Aborted", "AbortError"));
                return;
            }

            const xhr = new XMLHttpRequest();
            xhr.open("GET", url, true);
            xhr.responseType = "arraybuffer";

            if (signal) {
                signal.addEventListener("abort", () => {
                    xhr.abort();
                    reject(new DOMException("Aborted", "AbortError"));
                });
            }

            if (onProgress) {
                xhr.onprogress = (event) => {
                    if (event.lengthComputable && event.total > 0) {
                        const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
                        onProgress(percent);
                    } else {
                        onProgress(-1);
                    }
                };
            }

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    const responseContentType = xhr.getResponseHeader("Content-Type") || "";
                    const blobType = forceContentType || responseContentType;
                    // xhr.response is an ArrayBuffer — wrap as Blob with the chosen MIME type
                    resolve(new Blob([xhr.response], { type: blobType }));
                } else {
                    reject(new Error("Download failed with status " + xhr.status));
                }
            };

            xhr.onerror = () => reject(new Error("Network error during download"));
            xhr.ontimeout = () => reject(new Error("Download timed out"));

            xhr.send();
        });
    };

    /**
     * Download a file and return a base64 data URL.
     * @param {string} [forceContentType] - Force the data URL MIME type (e.g. "application/pdf")
     */
    downloadAsBase64 = async (url, onProgress = null, forceContentType = null, signal = null) => {
        const blob = await this.downloadBlob(url, onProgress, forceContentType, signal);
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
        });
    };

    /**
     * Download a file and return an object URL.
     * Caller is responsible for calling URL.revokeObjectURL() when done.
     */
    downloadAsObjectUrl = async (url, onProgress = null, forceContentType = null, signal = null) => {
        const blob = await this.downloadBlob(url, onProgress, forceContentType, signal);
        return URL.createObjectURL(blob);
    };
}

export default new S3DownloadService();
